import { useEffect, useId, useState } from 'react';

import { AboutPanel } from '@/components/settings/AboutPanel';
import { testApiKey, type TestKeyResult } from '@/lib/api';
import { deleteSetting, getSetting, putSetting } from '@/lib/db';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';

export const API_KEY_STORAGE_KEY = 'cdc:apiKey';
export const MODEL_STORAGE_KEY = 'cdc:modelId';
export const DEFAULT_MODEL = 'claude-opus-4-7';

const AVAILABLE_MODELS = [
  { id: 'claude-opus-4-7', label: 'Claude Opus 4.7' },
  { id: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6' },
  { id: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5' },
] as const;

type TestStatus =
  | { kind: 'idle' }
  | { kind: 'pending' }
  | { kind: 'ok'; model: string }
  | { kind: 'error'; message: string };

interface SettingsDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function readStored(key: string): string {
  if (typeof window === 'undefined') return '';
  return window.localStorage.getItem(key) ?? '';
}

function describeError(result: Extract<TestKeyResult, { ok: false }>): string {
  if (result.message) return result.message;
  const err = result.error;
  if (err && typeof err === 'object') {
    const inner = (err as { error?: { message?: unknown }; message?: unknown });
    if (typeof inner.message === 'string') return inner.message;
    if (inner.error && typeof inner.error === 'object' && typeof inner.error.message === 'string') {
      return inner.error.message;
    }
  }
  return `Request failed${result.status ? ` (HTTP ${result.status})` : ''}.`;
}

export function SettingsDrawer({ open, onOpenChange }: SettingsDrawerProps) {
  const keyInputId = useId();
  const modelInputId = useId();

  const [apiKey, setApiKey] = useState(() => readStored(API_KEY_STORAGE_KEY));
  const [model, setModel] = useState(
    () => readStored(MODEL_STORAGE_KEY) || DEFAULT_MODEL,
  );
  const [status, setStatus] = useState<TestStatus>({ kind: 'idle' });

  useEffect(() => {
    if (!open) setStatus({ kind: 'idle' });
  }, [open]);

  // Hydrate from IDB on mount; reconcile with localStorage so the next paint matches truth.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [storedKey, storedModel] = await Promise.all([
        getSetting('apiKey'),
        getSetting('modelId'),
      ]);
      if (cancelled) return;
      const lsKey = readStored(API_KEY_STORAGE_KEY);
      if (storedKey?.value) {
        if (storedKey.value !== lsKey) {
          window.localStorage.setItem(API_KEY_STORAGE_KEY, storedKey.value);
        }
        setApiKey(storedKey.value);
      } else if (lsKey) {
        await putSetting({ id: 'apiKey', value: lsKey });
      }
      const resolvedModel = storedModel?.value || readStored(MODEL_STORAGE_KEY) || DEFAULT_MODEL;
      setModel(resolvedModel);
      window.localStorage.setItem(MODEL_STORAGE_KEY, resolvedModel);
      if (!storedModel) {
        await putSetting({ id: 'modelId', value: resolvedModel });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function persist(nextKey: string, nextModel: string): Promise<void> {
    if (nextKey) {
      window.localStorage.setItem(API_KEY_STORAGE_KEY, nextKey);
      await putSetting({ id: 'apiKey', value: nextKey });
    } else {
      window.localStorage.removeItem(API_KEY_STORAGE_KEY);
      await deleteSetting('apiKey');
    }
    window.localStorage.setItem(MODEL_STORAGE_KEY, nextModel);
    await putSetting({ id: 'modelId', value: nextModel });
  }

  async function handleSave() {
    await persist(apiKey.trim(), model.trim() || DEFAULT_MODEL);
    onOpenChange(false);
  }

  async function handleClear() {
    setApiKey('');
    setStatus({ kind: 'idle' });
    window.localStorage.removeItem(API_KEY_STORAGE_KEY);
    await deleteSetting('apiKey');
  }

  async function handleTest() {
    const trimmed = apiKey.trim();
    if (!trimmed) {
      setStatus({ kind: 'error', message: 'Paste an API key first.' });
      return;
    }
    setStatus({ kind: 'pending' });
    try {
      const result = await testApiKey(trimmed, model.trim() || DEFAULT_MODEL);
      if (result.ok) {
        setStatus({ kind: 'ok', model: result.model });
        await persist(trimmed, model.trim() || DEFAULT_MODEL);
      } else {
        setStatus({ kind: 'error', message: describeError(result) });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Network error.';
      setStatus({ kind: 'error', message });
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle className="text-lg font-semibold">Settings</SheetTitle>
          <SheetDescription className="text-sm text-neutral-500">
            Bring your own Anthropic API key. Stored locally; never sent anywhere except the
            Anthropic API via the local proxy.
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-col gap-2">
          <label htmlFor={keyInputId} className="text-sm font-medium">
            Anthropic API key
          </label>
          <Input
            id={keyInputId}
            type="password"
            autoComplete="off"
            spellCheck={false}
            placeholder="sk-ant-..."
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
          />
          <p className="text-xs text-amber-600 dark:text-amber-400">
            Stored in plaintext in your browser&rsquo;s localStorage. Anyone with access to this
            machine can read it.
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor={modelInputId} className="text-sm font-medium">
            Model
          </label>
          <select
            id={modelInputId}
            value={model}
            onChange={(e) => setModel(e.target.value)}
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            {AVAILABLE_MODELS.map((m) => (
              <option key={m.id} value={m.id}>
                {m.label}
              </option>
            ))}
          </select>
        </div>

        <TestStatusBadge status={status} />

        <AboutPanel />

        <SheetFooter>
          <SheetClose asChild>
            <Button variant="ghost">Cancel</Button>
          </SheetClose>
          <Button variant="destructive" onClick={handleClear} disabled={!apiKey}>
            Clear
          </Button>
          <Button
            variant="secondary"
            onClick={handleTest}
            disabled={status.kind === 'pending' || !apiKey}
          >
            {status.kind === 'pending' ? 'Testing…' : 'Test'}
          </Button>
          <Button onClick={handleSave} disabled={!apiKey}>
            Save
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function TestStatusBadge({ status }: { status: TestStatus }) {
  if (status.kind === 'idle') return null;
  if (status.kind === 'pending') {
    return <p className="text-sm text-neutral-500">Pinging Anthropic…</p>;
  }
  if (status.kind === 'ok') {
    return (
      <p className="text-sm text-emerald-600 dark:text-emerald-400">
        Key works. Model {status.model} responded.
      </p>
    );
  }
  return (
    <p className="text-sm text-red-600 dark:text-red-400" role="alert">
      {status.message}
    </p>
  );
}
